import { Box, Button, Card, Grid, Stack, Typography } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BackButton } from "src/common/components/BackButton";
import { useAccess } from "src/hooks/useAccess.hook";
import { useCustomer, useGetHistory } from "src/hooks/useFetch.hook";
import { FEATURE } from "src/util/enums";
import { BaseTable } from "../BaseTable/BaseTable";
import Page from "../Page";
import { WalletHistoryListColumns } from "./walletHistory.constant";
import { downloadWalletStatementPdf } from "./walletStatementPdf";
import { useDomainConfig } from "src/hooks/useDomainConfig";
import { GridLabel } from "../Transactions/TransactionView";
import { fDateTime } from "src/utils/formatTime";

// embedded: rendered as the Wallet History tab of the customer form, which
// already shows the page chrome and customer details.
export default function WalletHistoryList({ embedded = false }) {
  const { id: customerId } = useParams();
  const { data: customerDetail, isFetching: isCustomerDetailFetching } =
    useCustomer(customerId);
  const { data: history, isFetching } = useGetHistory(
    "walletHistoryList",
    customerId
  );
  const isReadOnly = useAccess(FEATURE.CUSTOMERS, "R");
  const domainConfig = useDomainConfig();
  const [tableData, setTableData] = useState();
  const [customer, setCustomer] = useState();

  useEffect(() => {
    let data = [];
    if (!isFetching) {
      if (history?.data?.length > 0) {
        // Rows have no unique id of their own (id is the tag pk); they come
        // newest first and the order is stable, so the index identifies a row.
        data = history.data.map((row, index) => ({
          ...row,
          rowKey: `${index}-${row.modify_time}`,
        }));
      }
      setTableData(data);
    }
  }, [isFetching]);
  useEffect(() => {
    let data = [];
    if (!isCustomerDetailFetching) {
      if (customerDetail?.data?.length > 0) {
        data = customerDetail.data[0];
      }
      setCustomer(data);
    }
  }, [isCustomerDetailFetching]);

  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      await downloadWalletStatementPdf({
        rows: tableData,
        customer,
        customerId,
        brand: domainConfig?.name || "Amik",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // The customer form already wraps its tabs in a Card.
  const HistoryContainer = embedded ? Box : Card;
  const historyTable = (
    <HistoryContainer sx={embedded ? {} : { p: 2, mt: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Wallet History</Typography>
        {tableData?.length > 0 && (
          <Stack direction="row" spacing={2} alignItems="baseline">
            <Typography variant="subtitle1" color="text.secondary">
              Current balance:{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color:
                    Number(tableData[0].current_balance) < 0
                      ? "error.main"
                      : "text.primary",
                }}
              >
                {Number(tableData[0].current_balance).toFixed(2)}
              </Box>
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleDownloadPdf}
              disabled={isCustomerDetailFetching || isDownloading}
            >
              {isDownloading ? "Preparing..." : "Download PDF"}
            </Button>
          </Stack>
        )}
      </Stack>

      <BaseTable
        rows={tableData ?? []}
        columns={WalletHistoryListColumns()}
        loading={isFetching}
        getRowId={(row) => row.rowKey}
        getRowHeight={() => "auto"}
        pageSize={10}
        isReadOnly={isReadOnly}
      />
    </HistoryContainer>
  );

  if (embedded) return historyTable;

  return (
    <Page>
      <Card sx={{ p: 2 }}>
        <BackButton text={"Customer Information"} path="/cm-customer" />
        {customerDetail && (
          <Box sx={{ width: "100%" }}>
            <Grid
              container
              rowSpacing={1}
              columnSpacing={{ xs: 2, sm: 3, md: 4 }}
            >
              <GridLabel
                label="ID"
                value={customer?.id}
                xs={12}
                sm={6}
                md={4}
              />
              <GridLabel
                label="Name"
                value={customer?.firstName ? customer?.firstName : ""}
                xs={12}
                sm={6}
                md={4}
              />
              <GridLabel
                xs={12}
                sm={6}
                md={4}
                label="Email"
                value={customer?.email}
              />
              <GridLabel
                xs={12}
                sm={6}
                md={4}
                label="Phone"
                value={customer?.phone}
              />
              <GridLabel
                xs={12}
                sm={6}
                md={4}
                label="Tag PK"
                value={customer?.ocpp_tag_pk}
              />
              <GridLabel
                xs={12}
                sm={6}
                md={4}
                label="Created At"
                value={fDateTime(customer?.createdAt, "yyyy-MM-dd hh:mm a")}
              />
            </Grid>
          </Box>
        )}
      </Card>
      {historyTable}
    </Page>
  );
}
